import { useState, useCallback, useEffect } from 'react';
import { Paper, Tooltip } from '@mui/material';
import { Spinner, Input, FormGroup, Label, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import {
  getVIRVendors, createVIRVendor, updateVIRVendor, deleteVIRVendor, getVIRCategories,
} from './FormikHelpers/Api';

const LABEL_STYLE = {
  fontSize: '0.75rem', fontWeight: 700, color: '#334155',
  textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6,
};

const selectStyles = {
  control: (base, { isFocused }) => ({
    ...base,
    borderColor: isFocused ? '#4F46E5' : '#E2E8F0',
    borderRadius: 8, minHeight: 36, fontSize: '0.83rem',
    boxShadow: isFocused ? '0 0 0 3px rgba(79,70,229,0.12)' : 'none',
    '&:hover': { borderColor: '#4F46E5' },
  }),
  option: (base, { isSelected, isFocused }) => ({
    ...base, fontSize: '0.83rem',
    background: isSelected ? '#4F46E5' : isFocused ? '#EEF2FF' : 'white',
    color: isSelected ? 'white' : '#0F172A',
  }),
  menu: (base) => ({ ...base, borderRadius: 8, border: '1px solid #E2E8F0', zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  multiValue: (base) => ({ ...base, background: '#EEF2FF', borderRadius: 6 }),
  multiValueLabel: (base) => ({ ...base, color: '#4338CA', fontWeight: 600, fontSize: '0.78rem' }),
  multiValueRemove: (base) => ({ ...base, color: '#4338CA', '&:hover': { background: '#C7D2FE', color: '#312E81' } }),
};

const emptyRepresentative = { title: '', contactName: '', email: '', phoneNo: '' };

const emptyVendor = {
  companyName: '', website: '', representatives: [{ ...emptyRepresentative }], categoryIds: [], isActive: true,
};

const RepresentativesEditor = ({ reps, onChange, disabled }) => {
  const add = () => onChange([...reps, { ...emptyRepresentative }]);
  const remove = (i) => onChange(reps.filter((_, idx) => idx !== i));
  const update = (i, field, val) =>
    onChange(reps.map((r, idx) => idx === i ? { ...r, [field]: val } : r));

  return (
    <div>
      {reps.map((rep, i) => (
        <div key={i} style={{
          background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10,
          padding: '14px 14px 10px', marginBottom: 10, position: 'relative',
        }}>
          <div style={{ fontSize: '0.70rem', fontWeight: 700, color: '#4F46E5', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 10 }}>
            Representative {i + 1}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label style={{ ...LABEL_STYLE, marginBottom: 4 }}>Title</Label>
              <Input
                value={rep.title}
                onChange={(e) => update(i, 'title', e.target.value)}
                placeholder="e.g. Account Manager"
                disabled={disabled}
                style={{ borderColor: '#E2E8F0', borderRadius: 7, fontSize: '0.82rem' }}
              />
            </FormGroup>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label style={{ ...LABEL_STYLE, marginBottom: 4 }}>Contact Name</Label>
              <Input
                value={rep.contactName}
                onChange={(e) => update(i, 'contactName', e.target.value)}
                placeholder="Full name"
                disabled={disabled}
                style={{ borderColor: '#E2E8F0', borderRadius: 7, fontSize: '0.82rem' }}
              />
            </FormGroup>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label style={{ ...LABEL_STYLE, marginBottom: 4 }}>Email</Label>
              <Input
                type="email"
                value={rep.email}
                onChange={(e) => update(i, 'email', e.target.value)}
                placeholder="email@example.com"
                disabled={disabled}
                style={{ borderColor: '#E2E8F0', borderRadius: 7, fontSize: '0.82rem' }}
              />
            </FormGroup>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label style={{ ...LABEL_STYLE, marginBottom: 4 }}>Phone No.</Label>
              <Input
                type="tel"
                value={rep.phoneNo}
                onChange={(e) => update(i, 'phoneNo', e.target.value)}
                placeholder="+1 555 000 1234"
                disabled={disabled}
                style={{ borderColor: '#E2E8F0', borderRadius: 7, fontSize: '0.82rem' }}
              />
            </FormGroup>
          </div>
          {!disabled && reps.length > 1 && (
            <button
              type="button"
              onClick={() => remove(i)}
              style={{
                position: 'absolute', top: 10, right: 10,
                background: '#FEF2F2', border: '1px solid #FECDD3', borderRadius: 6,
                width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              }}
            >
              <FeatherIcon icon="trash-2" size={12} color="#DC2626" />
            </button>
          )}
        </div>
      ))}
      {!disabled && (
        <button
          type="button"
          onClick={add}
          style={{
            background: 'none', border: '1px dashed #C7D2FE', borderRadius: 8, padding: '7px 14px',
            color: '#4F46E5', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 4, width: '100%', justifyContent: 'center',
          }}
        >
          <FeatherIcon icon="plus" size={12} /> Add Representative
        </button>
      )}
    </div>
  );
};

const VendorFormModal = ({ isOpen, onClose, onSave, vendorData, isNew, categoryOptions, canEdit }) => {
  const [form, setForm] = useState({ ...emptyVendor });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    if (vendorData && !isNew) {
      setForm({
        companyName: vendorData.companyName || '',
        website: vendorData.website || '',
        representatives: vendorData.representatives?.length > 0
          ? vendorData.representatives
          : [{ ...emptyRepresentative }],
        categoryIds: (vendorData.categoryIds || []).map((c) =>
          typeof c === 'object' ? c._id || c.id : c
        ),
        isActive: vendorData.isActive !== undefined ? vendorData.isActive : true,
      });
    } else {
      setForm({ ...emptyVendor, representatives: [{ ...emptyRepresentative }] });
    }
    setErrors({});
  }, [isOpen, vendorData, isNew]);

  const set = (k) => (v) => {
    setForm((p) => ({ ...p, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.companyName?.trim()) errs.companyName = 'Company name is required';
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); toast.error('Please fix the errors'); return; }
    setSaving(true);
    try {
      await onSave({
        ...form,
        representatives: form.representatives.filter(
          (r) => r.contactName?.trim() || r.email?.trim() || r.phoneNo?.trim()
        ),
        isActive: form.isActive,
      }, isNew, vendorData?._id);
    } finally {
      setSaving(false);
    }
  };

  const selectedCats = (form.categoryIds || [])
    .map((id) => categoryOptions.find((o) => o.value === id))
    .filter(Boolean);

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader
        toggle={onClose}
        style={{ background: 'linear-gradient(135deg,#0F172A 0%,#1E293B 100%)', borderRadius: '10px 10px 0 0', padding: '16px 24px', borderBottom: '2px solid #4F46E5' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(99,102,241,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FeatherIcon icon="briefcase" size={16} color="#A5B4FC" />
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: '0.95rem', color: 'white' }}>
              {isNew ? 'Add Vendor' : 'Edit Vendor'}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'rgba(165,180,252,0.6)', marginTop: 1 }}>
              {isNew ? 'Create a new vendor profile' : `Editing: ${vendorData?.companyName || ''}`}
            </div>
          </div>
        </div>
      </ModalHeader>

      <ModalBody style={{ padding: '24px', background: '#F8FAFC', maxHeight: '75vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label style={LABEL_STYLE}>Company Name *</Label>
              <Input
                value={form.companyName}
                onChange={(e) => set('companyName')(e.target.value)}
                placeholder="e.g. Otis Elevator Co."
                disabled={!canEdit}
                style={{ borderColor: errors.companyName ? '#DC2626' : '#E2E8F0', borderRadius: 8, fontSize: '0.83rem' }}
              />
              {errors.companyName && <div style={{ fontSize: '0.72rem', color: '#DC2626', marginTop: 4 }}>{errors.companyName}</div>}
            </FormGroup>

            <FormGroup style={{ marginBottom: 0 }}>
              <Label style={LABEL_STYLE}>Website</Label>
              <Input
                type="url"
                value={form.website}
                onChange={(e) => set('website')(e.target.value)}
                placeholder="https://example.com"
                disabled={!canEdit}
                style={{ borderColor: '#E2E8F0', borderRadius: 8, fontSize: '0.83rem' }}
              />
            </FormGroup>
          </div>

          {!isNew && (
            <FormGroup style={{ marginBottom: 0 }}>
              <Label style={LABEL_STYLE}>Status</Label>
              <select
                value={form.isActive ? 'true' : 'false'}
                onChange={(e) => set('isActive')(e.target.value === 'true')}
                disabled={!canEdit}
                style={{
                  width: '100%', border: '1px solid #E2E8F0', borderRadius: 8,
                  fontSize: '0.83rem', padding: '7px 12px', outline: 'none', cursor: canEdit ? 'pointer' : 'not-allowed',
                  background: form.isActive ? '#ECFDF5' : '#FEF2F2',
                  color: form.isActive ? '#059669' : '#DC2626',
                  fontWeight: 700,
                }}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </FormGroup>
          )}

          <FormGroup style={{ marginBottom: 0 }}>
            <Label style={LABEL_STYLE}>Categories Served</Label>
            <Select
              options={categoryOptions}
              value={selectedCats}
              onChange={(opts) => set('categoryIds')((opts || []).map((o) => o.value))}
              placeholder="Select categories this vendor serves..."
              styles={selectStyles}
              isMulti
              isDisabled={!canEdit}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            <div style={{ fontSize: '0.71rem', color: '#94A3B8', marginTop: 4 }}>
              Vendors will only appear when creating reports for the categories they serve.
            </div>
          </FormGroup>

          <div>
            <Label style={LABEL_STYLE}>Representatives</Label>
            <RepresentativesEditor
              reps={form.representatives}
              onChange={set('representatives')}
              disabled={!canEdit}
            />
          </div>
        </div>
      </ModalBody>

      <ModalFooter style={{ background: 'white', padding: '14px 24px', gap: 8, borderTop: '1px solid #E2E8F0' }}>
        <button type="button" className="it-btn it-btn--cancel" onClick={onClose} disabled={saving}>
          <FeatherIcon icon="x" size={13} /> Cancel
        </button>
        {canEdit && (
          <button
            type="button"
            className={`it-btn it-btn--save${saving ? ' it-btn--saving' : ''}`}
            onClick={handleSave}
            disabled={saving}
            style={{ minWidth: 130 }}
          >
            {saving ? <Spinner size="sm" /> : <FeatherIcon icon="check" size={13} />}
            {saving ? 'Saving...' : isNew ? 'Add Vendor' : 'Save Changes'}
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
};

const VIRVendorsTable = ({ userRole, isLead, onRefresh }) => {
  const [vendors, setVendors] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [isNew, setIsNew] = useState(false);

  const role = (userRole || '').toLowerCase();
  const canManage = role === 'company' || (role === 'technical staff' && isLead);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await getVIRCategories({ activeOnly: 'true' });
      setCategoryOptions((res?.data || []).map((c) => ({ value: c._id, label: c.name })));
    } catch {
      toast.error('Failed to load categories');
    }
  }, []);

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (!showInactive) params.activeOnly = 'true';
      if (filterCategoryId) params.categoryId = filterCategoryId;
      const res = await getVIRVendors(params);
      setVendors(res?.data || []);
    } catch {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, [showInactive, filterCategoryId]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const handleSave = async (data, newVendor, existingId) => {
    if (newVendor) {
      await createVIRVendor(data);
    } else {
      await updateVIRVendor(existingId, data);
    }
    setModalOpen(false);
    fetchVendors();
    onRefresh?.();
  };

  const handleDelete = async (vendor) => {
    const res = await Swal.fire({
      title: 'Deactivate Vendor?',
      html: `<p>Deactivate <strong>${vendor.companyName}</strong>?</p>`,
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#DC2626', confirmButtonText: 'Deactivate',
      cancelButtonText: 'Cancel', width: 420,
    });
    if (!res.isConfirmed) return;
    try {
      await deleteVIRVendor(vendor._id);
      toast.success('Vendor deactivated');
      fetchVendors();
    } catch {
      toast.error('Failed to deactivate vendor');
    }
  };

  const openNew = () => { setSelectedVendor(null); setIsNew(true); setModalOpen(true); };
  const openEdit = (v) => { setSelectedVendor(v); setIsNew(false); setModalOpen(true); };

  const allCatOptions = [{ value: '', label: 'All Categories' }, ...categoryOptions];
  const filterCatVal = allCatOptions.find((o) => o.value === filterCategoryId) || allCatOptions[0];

  return (
    <>
      <Paper elevation={0} className="it-wrap it-paper">
        <div className="it-header">
          <div className="it-header__left">
            <div className="it-header__icon-wrap">
              <FeatherIcon icon="briefcase" size={18} color="#A5B4FC" />
            </div>
            <div className="it-header__meta">
              <div className="it-header__label">Configuration</div>
              <div className="it-header__title">Vendors</div>
            </div>
            {vendors.length > 0 && (
              <div className="it-header__badges">
                <span className="it-header__badge it-header__badge--records">
                  {vendors.length} vendor{vendors.length !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>
          <div className="it-header__actions" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 180 }}>
              <Select
                options={allCatOptions}
                value={filterCatVal}
                onChange={(opt) => setFilterCategoryId(opt?.value || '')}
                styles={{
                  ...selectStyles,
                  control: (base, state) => ({ ...selectStyles.control(base, state), minHeight: 32, background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(165,180,252,0.3)' }),
                  singleValue: (base) => ({ ...base, color: 'rgba(165,180,252,0.9)', fontSize: '0.78rem' }),
                  placeholder: (base) => ({ ...base, color: 'rgba(165,180,252,0.5)', fontSize: '0.78rem' }),
                }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                placeholder="All Categories"
              />
            </div>
            {canManage && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'rgba(165,180,252,0.75)', fontSize: '0.78rem', cursor: 'pointer', marginBottom: 0 }}>
                <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} style={{ cursor: 'pointer' }} />
                Show inactive
              </label>
            )}
            {canManage && (
              <button type="button" className="it-btn it-btn--primary-solid it-add" onClick={openNew}>
                <FeatherIcon icon="plus" size={14} /> Add Vendor
              </button>
            )}
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="it-tbl" style={{ minWidth: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th style={{ minWidth: 180 }}>Company Name</th>
                <th style={{ minWidth: 160 }}>Website</th>
                <th style={{ minWidth: 240 }}>Representatives</th>
                <th style={{ minWidth: 200 }}>Categories Served</th>
                <th style={{ width: 90 }}>Status</th>
                {canManage && <th style={{ width: 100 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={canManage ? 7 : 6} style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Spinner style={{ color: '#4F46E5' }} />
                  </td>
                </tr>
              )}
              {!loading && vendors.length === 0 && (
                <tr>
                  <td colSpan={canManage ? 7 : 6} className="it-empty-cell">
                    <div className="it-empty" style={{ minHeight: '25vh' }}>
                      <div className="it-empty__icon-wrap">
                        <FeatherIcon icon="briefcase" size={24} color="#4F46E5" />
                      </div>
                      <div className="it-empty__title">No Vendors</div>
                      <div className="it-empty__sub">
                        {canManage ? 'Click "Add Vendor" to create your first vendor.' : 'No vendors configured yet.'}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && vendors.map((v) => (
                <tr key={v._id} className="it-row" onClick={() => openEdit(v)} style={{ cursor: 'pointer' }}>
                  <td className="it-expand-td">
                    <div className="it-expand-icon it-expand-icon--view">
                      <FeatherIcon icon="edit-3" size={12} color="#4F46E5" />
                    </div>
                  </td>
                  <td>
                    <span style={{ fontWeight: 700, color: '#0F172A', fontSize: '0.82rem' }}>{v.companyName}</span>
                  </td>
                  <td>
                    {v.website
                      ? <a href={v.website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ fontSize: '0.78rem', color: '#4F46E5' }}>{v.website.replace(/^https?:\/\//, '')}</a>
                      : <span className="it-cell-muted">—</span>}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {v.representatives && v.representatives.length > 0
                        ? v.representatives.map((rep, i) => (
                            <div key={i} style={{ fontSize: '0.78rem', borderBottom: i < v.representatives.length - 1 ? '1px dashed #E2E8F0' : 'none', paddingBottom: i < v.representatives.length - 1 ? 4 : 0 }}>
                              <span style={{ fontWeight: 600, color: '#0F172A' }}>{rep.contactName || '—'}</span>
                              {rep.title && <span style={{ color: '#64748B' }}> · {rep.title}</span>}
                              {rep.email && (
                                <div>
                                  <a href={`mailto:${rep.email}`} onClick={(e) => e.stopPropagation()} style={{ color: '#4F46E5', fontSize: '0.72rem' }}>{rep.email}</a>
                                </div>
                              )}
                              {rep.phoneNo && (
                                <div style={{ color: '#64748B', fontSize: '0.72rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                                  <FeatherIcon icon="phone" size={10} color="#94A3B8" /> {rep.phoneNo}
                                </div>
                              )}
                            </div>
                          ))
                        : <span className="it-cell-muted">—</span>}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {(v.categoryIds || []).length > 0
                        ? (v.categoryIds || []).map((cat, i) => (
                            <span key={i} style={{
                              background: '#EEF2FF', color: '#4338CA', border: '1px solid #C7D2FE',
                              borderRadius: 6, padding: '2px 8px', fontSize: '0.70rem', fontWeight: 600,
                            }}>
                              {typeof cat === 'object' ? cat.name : (categoryOptions.find((o) => o.value === cat)?.label || cat)}
                            </span>
                          ))
                        : <span className="it-cell-muted">—</span>}
                    </div>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`it-badge ${v.isActive ? 'it-badge--ok' : 'it-badge--overdue'}`}>
                      {v.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {canManage && (
                    <td className="it-actions-td" onClick={(e) => e.stopPropagation()}>
                      <div className="it-action-group">
                        <Tooltip title="Edit" arrow>
                          <button type="button" className="it-ebtn" onClick={(e) => { e.stopPropagation(); openEdit(v); }}>
                            <FeatherIcon icon="edit-2" size={13} color="#4F46E5" />
                          </button>
                        </Tooltip>
                        <Tooltip title="Deactivate" arrow>
                          <button type="button" className="it-dbtn" onClick={(e) => { e.stopPropagation(); handleDelete(v); }}>
                            <FeatherIcon icon="trash-2" size={13} color="#DC2626" />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>

      <VendorFormModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        vendorData={selectedVendor}
        isNew={isNew}
        categoryOptions={categoryOptions}
        canEdit={canManage}
      />
    </>
  );
};

export { VIRVendorsTable };
